import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as moment from 'moment';
import {SoilTypeService} from '../../../../../services/soilType.service';
import SoilTypeModel from '../../../../../services/soilTypeModel';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * SoilTypeComponent
 *
 * This component is responsible for managing Soil Types within the Pond Management module.
 * It provides a user interface for administrators to view, create, update, and search for soil types.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all soil types.
 * - Provide search functionality to filter soil types by name or abbreviation.
 * - Manage the creation of new soil types with validation.
 * - Handle updates to existing soil types.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update soil types.
 * - **Real-time Search**: Filter list by soil type name or abbreviation.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures soil type names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all soil types from `SoilTypeService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateSoilType` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-soil-type
 * @example
 * <app-soil-type></app-soil-type>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-soil-type',
  templateUrl: './soil-type.component.html',
  styleUrls: ['./soil-type.component.scss']
})
export class SoilTypeComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched soil types from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {SoilTypeModel[]}
   * @default []
   */
  public soilTypes: SoilTypeModel[] = [];

  /**
   * Array holding the currently displayed soil types.
   * This list is populated by the BatchLoader (subset of soilTypes or filteredSoilTypes).
   * @type {SoilTypeModel[]}
   * @default []
   */
  public soilTypesList: SoilTypeModel[] = [];

  /**
   * Array holding soil types that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {SoilTypeModel[]}
   * @default []
   */
  public filteredSoilTypes: SoilTypeModel[] = [];

  /**
   * Model for the current soil type being added or edited.
   * Bound to the form for data manipulation.
   * @type {SoilTypeModel}
   */
  public soilType: SoilTypeModel = new SoilTypeModel();

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  submitted = false;

  /**
   * Reactive form group for soil type details (name, abbreviation, description).
   * @type {FormGroup}
   */
  addSoilTypeForm: FormGroup;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public soilTypeDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for SoilTypeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {SoilTypeService} soilTypeService - Service for Soil Type API operations.
   * @service SoilTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public soilTypeService: SoilTypeService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.soilType = new SoilTypeModel();
    /*form validation*/
    this.addSoilTypeForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.addSoilTypeForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all soil types from the backend.
   * 5. Formats the 'updatedAt' date for each soil type.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.soilTypes = [];
    this.soilTypesList = [];
    this.filteredSoilTypes = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      if (this.soilTypes.length > 0) {
        this.soilTypes.forEach(eachSoilType => {
          eachSoilType.updatedAt = JSON.parse(JSON.stringify(moment(eachSoilType.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSoilTypes : this.soilTypes,
      (updatedList) => {
        this.soilTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.soilTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method soilTypeFormValidation
   * @returns {AbstractControl} Form controls
   */
  public get soilTypeFormValidation() {
    return this.addSoilTypeForm.controls;
  }

  /**
   * Filters the soil type list based on the search query.
   *
   * - Filters by name or abbreviation (case-insensitive).
   * - Updates `filteredSoilTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredSoilTypes`, `soilTypesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.soilTypes.filter(route =>
        route.name?.toLowerCase().includes(query) ||
        route.abbreviation?.toLowerCase().includes(query)
      )
      : this.soilTypes;
    this.filteredSoilTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.soilTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Handles the submission of the Soil Type form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateSoilType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateSoilType() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.addSoilTypeForm.invalid) {
      return;
    }
    try {
      this.soilTypeDoubleClick = true;
      // GET FORM VALUES
      const formData = this.addSoilTypeForm.value; // contains updated values
      if (this.submitButton) {
        await this.soilTypeService.create(formData);
        this.toasterService.success('Soil type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateSoilType = {...formData}
        delete updateSoilType.id;
        await this.soilTypeService.updateOneByID(this.soilType.id, updateSoilType);
        this.toasterService.success('Soil type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.soilTypeDoubleClick = false;
    }
  }

  /**
   * Retrieves a Soil Type by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getSoilTypeById
   * @param {string} id - The ID of the soil type to edit.
   * @returns {void}
   */
  public getSoilTypeById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.soilTypeDoubleClick = false;
    this.updateButton = true;
    const foundSoilType = this.soilTypesList.find(uom => uom?.id === id);
    this.soilType = foundSoilType ? {...foundSoilType} : new SoilTypeModel(); // clone object
    if (this.soilType) {
      this.addSoilTypeForm.patchValue({
        name: this.soilType.name,
        abbreviation: this.soilType.abbreviation,
        description: this.soilType.description
      });
    }
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.addSoilTypeForm.reset();
    this.showForm = false;
    this.soilType = new SoilTypeModel();
    this.soilTypeDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.addSoilTypeForm.controls).length;
  }
}
